import { Avatar, Box, Typography } from "@mui/material";
import React from "react";
import theme from "Theme/AppTheme";
import { UserWithFullName } from "components/Signals/Dashboard/DashboardMenu/ShareDialog/constants";

export const MENTION_DROPDOWN_WIDTH = 368;

type MentionDropdownProps = {
    id: string;
    testId: string;
    position: { top: number; left: number } | null;
    isOpen: boolean;
    users: UserWithFullName[];
    activeIndex: number;
    dropdownRef: React.RefObject<HTMLDivElement | null>;
    getOptionId: (user: UserWithFullName, index: number) => string;
    onHover: (index: number) => void;
    onSelect: (user: UserWithFullName) => void;
};

const MentionDropdown = React.memo(
    ({
        id,
        testId,
        position,
        isOpen,
        users,
        activeIndex,
        dropdownRef,
        getOptionId,
        onHover,
        onSelect,
    }: MentionDropdownProps) => {
        if (!isOpen || !position) return null;
        return (
            <Box
                sx={{
                    position: "fixed",
                    top: position.top,
                    left: position.left,
                    width: MENTION_DROPDOWN_WIDTH,
                    backgroundColor: theme.palette.white.main,
                    border: `1px solid ${theme.palette.gray[200]}`,
                    borderRadius: 1.5,
                    boxShadow: "0 10px 20px rgba(15, 23, 42, 0.08)",
                    maxHeight: 220,
                    overflowY: "auto",
                    zIndex: 10,
                }}
                data-testid={testId}
                id={id}
                role="listbox"
                ref={dropdownRef}
                onMouseDown={(event) => {
                    event.preventDefault();
                }}
            >
                {users.map((user, index) => (
                    <MentionUserOption
                        key={user.userId}
                        user={user}
                        id={getOptionId(user, index)}
                        isActive={index === activeIndex}
                        onHover={() => onHover(index)}
                        onSelect={() => onSelect(user)}
                    />
                ))}
            </Box>
        );
    },
);

const MentionUserOption = ({
    user,
    id,
    isActive,
    onHover,
    onSelect,
}: {
    user: UserWithFullName;
    id: string;
    isActive: boolean;
    onHover: () => void;
    onSelect: () => void;
}) => (
    <Box
        id={id}
        data-testid="comment-mention-option"
        role="option"
        aria-selected={isActive}
        onClick={onSelect}
        onMouseEnter={onHover}
        onMouseDown={(event) => {
            event.preventDefault();
        }}
        sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            padding: "8px 12px",
            cursor: "pointer",
            "&:hover": {
                backgroundColor: theme.palette.gray[50],
            },
            ...(isActive && {
                backgroundColor: theme.palette.gray[50],
            }),
        }}
    >
        <Avatar
            sx={{
                width: 28,
                height: 28,
                backgroundColor: theme.palette.primary[100],
                fontSize: "12px",
                fontWeight: 700,
                color: theme.palette.gray[700],
            }}
        >
            {user.fullName.charAt(0)}
        </Avatar>
        <Box>
            <Typography
                sx={{
                    fontSize: "13px",
                    fontWeight: 600,
                    color: theme.palette.gray[800],
                }}
            >
                {user.fullName}
            </Typography>
            <Typography
                sx={{
                    fontSize: "12px",
                    color: theme.palette.gray[500],
                }}
            >
                {user.email}
            </Typography>
        </Box>
    </Box>
);

export default MentionDropdown;
